import React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { getRegion, getRegionCentreOfGravity } from 'reducers/region';
import { riskColors } from 'utils/constants/risks';
import Map from 'components/Map';
import { getRiskLevelByValue } from 'utils/helper/zone';
import {
  DrawingOptions,
  HIGH_DETAIL_CONFIG,
  LOCATION_MODE,
  LOW_DETAIL_CONFIG,
} from 'utils/constants/Map';
import { PAGE_NAME } from 'utils/constants';
import useZone from 'containers/Pages/Locations/Zones/Layouts/useZone';
import useSites from 'containers/Pages/Locations/Sites/Layouts/useSite';
import { zoneSelectors } from 'containers/Pages/Locations/Zones';
import useRegions from 'reducers/customHooks/useRegions';
import { getUserLocation } from 'reducers/users';

export const getModeConfiguration = mode => {
  switch (mode) {
    case LOCATION_MODE.LOW_DETAIL:
      return LOW_DETAIL_CONFIG;

    case LOCATION_MODE.HIGH_DETAIL:
      return HIGH_DETAIL_CONFIG;

    default:
      return {
        strokeOpacity: 1,
      };
  }
};

const MapWithRegions = ({
  center,
  markers,
  region,
  secondaryPolygon,
  secondaryPolygonMode = LOCATION_MODE.LOW_DETAIL,
  zonePolygonMode = LOCATION_MODE.LOW_DETAIL,
  drawingStyle,
  zoneFilters,
  requirePan,
  onZoneClick,
  selectedSecondaryPolygon,
  selectedZone,
  secondaryDetail,
  displaySites = false,
  activeSite = '',
  panTo,
  polygonsInForm = true,
  selectedRegion,
  centerDefault,
  eventPolygon,
  ...props
}) => {
  const zones = useZone(zoneFilters);
  const regions = useRegions();
  const zonesAndRegions = [...zones, ...regions];
  const sites = useSites();
  //-----------helps in panning to a polygon-------------------------------------
  const panToLocation = () => {
    const detailValues =
      selectedZone || selectedRegion
        ? zonesAndRegions.find(
            item => item.id === (selectedZone || selectedRegion),
          )
        : secondaryDetail;

    // pans to distress markers and sets the boundary
    if (props.distress && !isEmpty(props?.polylines.markers)) {
      return {
        highLightBoundary: props.polylines.markers,
      };
    }
    if (detailValues) {
      //Note: uncomment this incase zooming works abnormaly on map
      // const { latitude: lat, longitude: lng } = detailValues.centreOfGravity;
      return {
        // panToLocation: { lat, lng },
        highLightBoundary:
          selectedZone || selectedRegion
            ? detailValues.boundary
            : detailValues.locationDetail[0].paths,
      };
    }

    if (panTo) {
      const [{ lat, lng }] = panTo;
      return { panToLocation: { lat, lng } };
    }
    return {
      panToLocation: { lat: centerDefault?.lat, lng: centerDefault?.lng },
      highLightBoundary: undefined,
    };
  };
  //-----------------------------------------

  const polygons = () => {
    let polygon = [];
    if (secondaryPolygon) {
      polygon = secondaryPolygon.map(item => {
        return {
          ...getModeConfiguration(
            selectedSecondaryPolygon && item.id === selectedSecondaryPolygon
              ? LOCATION_MODE.HIGH_DETAIL
              : item.locationMode || secondaryPolygonMode,
          ),
          id: item.id,
          ...item,
        };
      });
    }
    if (!isEmpty(region)) {
      const concatPolygons = zones.map(zone => {
        return {
          id: zone.id,
          infoWindowData: {
            ZoneName: {
              type: 'text',
              value: zone.zone,
            },
            Consequence: {
              type: 'text',
              value: zone.consequence,
            },
            Hazard: {
              type: 'text',
              value: zone.hazard,
            },
            Likelihood: {
              type: 'text',
              value: zone.Likelihood,
            },
            RiskLevel: {
              type: 'text',
              value: zone.riskLevel,
            },
          },
          pageName: zone.pageName,
          layer: PAGE_NAME.ZONES,
          color: riskColors[getRiskLevelByValue(zone.riskLevel)],
          isFilled: true,
          paths: zone.boundary,
          ...getModeConfiguration(
            zone.id === selectedZone
              ? LOCATION_MODE.HIGH_DETAIL
              : zonePolygonMode,
          ),
          ...(onZoneClick && { onPolygonClick: onZoneClick }),
        };
      });
      if (displaySites) {
        const locationList = sites.filter(
          item =>
            item.id !== (activeSite.others ? activeSite.others.id.value : ''),
        );
        const sitePolygons = locationList.map(item => {
          const location = item.locationDetail;
          return {
            ...location[0],
            ...getModeConfiguration(LOCATION_MODE.LOW_DETAIL),
          };
        });

        concatPolygons.push(...sitePolygons);
      }
      if (!isEmpty(polygon)) concatPolygons.push(...polygon);
      return concatPolygons;
    } else if (!isEmpty(regions)) {
      const regionPolygons = regions.map(region => {
        return {
          id: region.id,
          infoWindowData: {
            regionName: {
              type: 'text',
              value: region.regionName,
            },
            riskLevel: {
              type: 'text',
              value: region.riskLevel,
            },
          },
          pageName: region.pageName,
          color: riskColors['REGION'],
          isFilled: true,
          paths: region.boundary,
          ...getModeConfiguration(
            region.id === selectedRegion
              ? LOCATION_MODE.HIGH_DETAIL
              : LOCATION_MODE.LOW_DETAIL,
          ),
          ...(onZoneClick && { onPolygonClick: onZoneClick }),
        };
      });
      if (!isEmpty(polygon)) regionPolygons.push(...polygon);

      return regionPolygons;
    } else {
      return polygon;
    }
  };

  const getShape = () => {
    switch (drawingStyle) {
      case DrawingOptions.rectangle:
        return {
          rectangles: polygons(),
        };

      default:
        return { polygons: polygons() };
    }
  };

  return (
    <Map
      center={center}
      markers={markers}
      {...getShape()}
      {...props}
      {...(requirePan && { ...panToLocation() })}
      {...(eventPolygon && {
        // pan to event polygon
        panToLocation: {
          lat: eventPolygon?.centreOfGravity.latitude,
          lng: eventPolygon?.centreOfGravity.longitude,
        },
        highLightBoundary: eventPolygon?.points?.map(m => ({
          lng: m.longitude,
          lat: m.latitude,
        })),
      })}
      polygonsInForm={polygonsInForm}
    />
  );
};

MapWithRegions.propTypes = {
  center: PropTypes.object,
  eventPolygon: PropTypes.object,
  region: PropTypes.object,
  markers: PropTypes.array,
  zones: PropTypes.array,
  sites: PropTypes.array,
  secondaryPolygon: PropTypes.array,
  displayRegionBoundary: PropTypes.bool,
  secondaryPolygonMode: PropTypes.oneOf(Object.values(LOCATION_MODE)),
  zonePolygonMode: PropTypes.oneOf(Object.values(LOCATION_MODE)),
  drawingStyle: PropTypes.oneOf(Object.values(DrawingOptions)),
  zonePanel: PropTypes.oneOf(Object.values(PAGE_NAME)),
  requirePan: PropTypes.bool,
  secondaryDetail: PropTypes.object,
  zoneFilters: PropTypes.object,
  onZoneClick: PropTypes.func,
  selectedSecondaryPolygon: PropTypes.string,
  selectedRegion: PropTypes.string,
  selectedZone: PropTypes.string,
  activeSite: PropTypes.object,
  displaySites: PropTypes.bool,
  polygonsInForm: PropTypes.bool,
  distress: PropTypes.bool,
  polylines: PropTypes.object,
  panTo: PropTypes.object,
  centerDefault: PropTypes.object,
};

const mapStateToProps = state => ({
  region: getRegion(state),
  zoneFilters: zoneSelectors.getZoneFilters(state),
  centerDefault: getUserLocation(state) || getRegionCentreOfGravity(state),
});

export default connect(mapStateToProps)(MapWithRegions);
